import { IGuestLogIn } from 'utils/api/models/eshop/guestLogIn';
import apiConfig from '../config';
import { env } from 'config/app.config';
import { ajax, AjaxResponse } from 'rxjs/ajax';
import { catchError, map, publish, refCount, share } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { pullRequestInProgress, pushRequestInProgress } from 'redux/modules/api/requestsInProgress/actions';
import { setToast } from 'redux/modules/toast/actions';
import { dispatch } from 'redux/store';
import textToHash from 'utils/converters/textToHash';
import { isRequestInProgress } from 'redux/modules/api/requestsInProgress/selectors';
import { cacheApiRequest, invalidateCache as invalidateCacheAction } from 'redux/modules/api/cache/actions';
import { isRequestCachedAndNotExpired } from 'redux/modules/api/cache/selectors';
import dateAfterTimestamp from 'utils/date/dateAfterTimestamp';
import models from 'utils/api/models/index';
import modelsResolver from 'utils/api/models/utils/modelsResolver';
import ErrorMapper from 'utils/api/errors/errorMapper';
import errorCodes from 'utils/api/errors/errorCodes';
import { AuthorizationService } from 'services/authorization/authorization';
import { ApiQuery } from 'utils/api';
import { translate } from 'components/Translation';
import { logOut, redirectUnauthorized } from 'redux/modules/session/actions';
import { EshopProductsService } from 'services/eshop/products';
import { store } from 'redux/store';
import { updateHeaders } from 'redux/modules/settings/headers/actions';
import { getLanguage } from 'utils/language/language';

interface params {
	source: string;
	endpointConfig: types.endpoints.IEndpointConfig;
	fullURL: types.ajax.url;
	method: types.ajax.method;
	payloadHash: number;
	invalidateKey?: ApiQuery;
}

interface errorObj {
	errorCode: string;
	errorMessage: string;
}

interface requestInProgress extends types.redux.api.requestInProgress.requestInProgress {}
interface errorInterface {
	errorCode: string;
	errorMessage: string;
}

export default class RxQuery {
	readonly source: string;
	private endpointConfig?: types.endpoints.IEndpointConfig;
	private payloadObject?: Object | null;
	private payloadHash: number = 0;
	private cacheTime: number = apiConfig.defaultCacheTime;
	private errorMsg?: string = 'Connecting error';
	private cache: boolean = true;
	private responseType: string = 'json';
	private contentType: string | 'AUTO' | 'DEFAULT' = 'DEFAULT';
	private model?: keyof typeof models = undefined;
	private errorHandling: boolean = false;
	private timeout: number = apiConfig.timeout[env];
	private formReference: Object | null = null;
	private invalidateKey?: ApiQuery;
	private logoutOnError: boolean = false;
	private isECommerceRequest: boolean = false;
	private accept: string = '*/*';
	private customHeaders: any = {};
	private allowConsecutiveCalls?: boolean;
	private host?: string | undefined;

	constructor(source: string) {
		this.source = source;
		this.isECommerceRequest = source === 'eCommerce';
	}

	private get params(): params {
		const source = apiConfig.sources[this.source][env];
		const protocol =
			env === 'DEV' ? 'http://' : apiConfig.sources[this.source].forceProtocol || apiConfig.protocol[env];
		const endpointConfig = this.endpointConfig || {
			url: '',
			method: 'GET',
		};
		const fullURL = endpointConfig ? (this.host ?? protocol + source) + endpointConfig.url : '';

		const method =
			endpointConfig && endpointConfig.method
				? endpointConfig.method
				: apiConfig.sources[this.source].defaultMethod || 'GET';

		return {
			source,
			endpointConfig,
			fullURL,
			method,
			payloadHash: this.payloadHash,
			invalidateKey: this.invalidateKey,
		};
	}

	private newRequestInProgress(rxAjax$?: Observable<AjaxResponse>): requestInProgress {
		return {
			url: this.params.fullURL,
			payloadHash: this.payloadHash,
			rxAjax$: rxAjax$,
			method: this.params.method,
		};
	}

	public getParams() {
		return this.params;
	}

	private cacheRequestIfNeeded(response: any, force = false) {
		const { source, endpointConfig, invalidateKey } = this.params;
		const validTill = dateAfterTimestamp(this.cacheTime);
		if (this.cache || force) {
			dispatch(
				cacheApiRequest({
					source,
					endpointUrl: endpointConfig.url,
					payloadHash: this.payloadHash,
					validTill,
					response,
					invalidateKey,
				})
			);
		}
	}

	public setEndpoint(endpointConfig: types.endpoints.IEndpointConfig) {
		this.endpointConfig = endpointConfig;
		return this;
	}

	public setPayload(payloadObject: Object | null) {
		this.payloadObject = payloadObject;
		this.payloadHash = textToHash(JSON.stringify(payloadObject));
		return this;
	}

	public setHost(host: string) {
		this.host = host;
		return this;
	}

	//custom headers required by omantel APIs
	public setCustomHeaders() {
		this.customHeaders = {
			'x-channel-id': 'EBU',
			'x-language': getLanguage(),
		};
		return this;
	}
	// to set single header
	public setHeaders(name: string, value: string | boolean, featureFlag?: string): this {
		if (featureFlag) {
			const isEnabled = store.getState().settings.remoteConfig?.sidebar?.[featureFlag];
			if (!isEnabled) return this;
		}

		this.customHeaders[name] = value;
		return this;
	}

	//This method is used to set an identifier to allow consecutive requests in certain scenarios when the previous request is not complete
	public setAllowConsecutiveCalls() {
		this.allowConsecutiveCalls = true;
		return this;
	}
	public setCacheTime(cacheTime?: number) {
		this.cacheTime = cacheTime || apiConfig.defaultCacheTime;
		return this;
	}

	public setResponseType(responseType: string) {
		this.responseType = responseType || apiConfig.defaultResponseType;
		return this;
	}

	public disableCache() {
		this.cache = false;
		return this;
	}

	public setTimeout(timeout: number) {
		this.timeout = timeout;
		return this;
	}

	public setErrorMsg(errorMsg: string) {
		this.errorMsg = errorMsg;
		return this;
	}

	public withErrorHandling(ref: Object | null = null) {
		this.errorHandling = true;
		this.formReference = ref;
		return this;
	}

	// DEBUG
	public print() {
		return JSON.stringify(
			{
				params: this.params,
				payloadObject: this.payloadObject,
			},
			null,
			2
		);
	}

	public unsetType() {
		this.model = undefined;
		return this;
	}

	public setType(modelName: keyof typeof models) {
		this.model = modelName;
		return this;
	}

	public setContentType(contentType: string) {
		this.contentType = contentType;
		return this;
	}

	public setAutoContentType() {
		this.contentType = 'AUTO';
		return this;
	}

	private asModel = (response: AjaxResponse) => {
		if (this.model && response.response) {
			return modelsResolver(response, this.model);
		} else {
			return response;
		}
	};

	private handleErrors(error: { response: any }) {
		error.response = error.response || { code: 'ERROR_TIMEOUT' };
		const mappedError = ErrorMapper.mapToMessage(error.response.code);

		const translatedError: errorInterface = {
			errorCode: error.response.code,
			errorMessage: mappedError,
		};

		if (error.response.code === 'INSUFFICIENT_RIGHTS') {
			dispatch(redirectUnauthorized());
			return;
		}

		const errorKey = error.response?.errors?.[0]?.errorKey;
		if (errorKey === 'ERR_COOKIE_EXPIRED' || errorKey === '_ERR_INVALID_COOKIE') {
			this.getOmantelIdentity();
		}

		if (this.errorHandling) {
			const errorCodeFromMap = errorCodes.get(translatedError.errorCode);
			if (errorCodeFromMap) {
				const { generalError, formField } = errorCodeFromMap;
				if (generalError) {
					dispatch(
						setToast({
							error: true,
							label: translatedError.errorMessage,
							actionType: translate('error'),
						})
					);
				} else {
					if (this.formReference !== null) {
						const { setFieldError, initialValues } = this.formReference as any;
						const formRefFieldNames = Object.keys(initialValues);
						const fieldExist = formRefFieldNames.find((fieldName) => formField.indexOf(fieldName) > -1);
						fieldExist
							? setFieldError(formField, translatedError.errorMessage)
							: dispatch(
									setToast({
										error: true,
										label: ErrorMapper.mapToMessage(translatedError.errorCode),
										actionType: translate('error'),
									})
							  );
					}
				}
			} else {
				dispatch(
					setToast({
						error: true,
						label: ErrorMapper.mapToMessage('AN_ERROR_OCCURED'),
						actionType: translate('error'),
					})
				);
			}
		}
	}

	public invalidateCache = (forAllUsers = false) => {
		const {
			source,
			endpointConfig: { url: endpointUrl },
			payloadHash,
		} = this.params;

		dispatch(
			invalidateCacheAction({
				source,
				endpointUrl,
				payloadHash: forAllUsers ? undefined : payloadHash,
			})
		);
	};

	private isRedirected = (response: string) => {
		return !!response && response.search('/Personal/') >= 0;
	};

	private checkForWrongApiResponse = (response: AjaxResponse) => {
		if (this.isRedirected(response.xhr.responseURL)) {
			dispatch(logOut());
			return;
		}
		if (response.response === null) {
			const error = {
				response: {
					code: 'WRONG_RESPONSE',
				},
			};
			this.handleErrors(error);
			return throwError(error);
		}
	};

	public setInvalidateKey = (key: ApiQuery) => {
		this.invalidateKey = key;
		return this;
	};

	public setLogoutOnError = () => {
		this.logoutOnError = true;
		return this;
	};

	public setIsECommerceRequest = () => {
		this.isECommerceRequest = true;
		return this;
	};

	public setAccept = (accept: string) => {
		this.accept = accept;
		return this;
	};

	public getOmantelIdentity = () => {
		EshopProductsService.getOmantelIdentity().subscribe(
			(data: IGuestLogIn) => {
				dispatch(
					updateHeaders({
						WCTrustedToken: data?.WCTrustedToken,
						WCToken: data?.WCToken,
						WCPersonalization: data?.personalizationID,
					})
				);
			},
			(error: any) => {
				throwError(error);
			}
		);
	};

	public setEshopIdentity = (WCTrustedToken?: string, WCToken?: string, WCPersonalization?: string) => {
		if (WCTrustedToken && WCToken) {
			this.customHeaders = { WCTrustedToken, WCToken, WCPersonalization };
		} else {
			this.customHeaders = store.getState()?.settings?.headers;
		}
		return this;
	};

	// final rxjs call
	private onCall(): Observable<AjaxResponse> {
		const { fullURL, method } = this.params;

		const ajaxConfig = {
			url: fullURL,
			method: method,
			timeout: this.timeout,
			body:
				(method === 'POST' || method === 'PUT' || method === 'DELETE') && this.payloadObject
					? this.payloadObject
					: undefined,
			withCredentials: !this.isECommerceRequest,
			responseType: this.responseType,
			redirect: 'follow',
		};

		if (this.accept !== '*/*') {
			if (this.payloadObject) {
				ajaxConfig['headers'] = {
					Accept: this.accept,
				};
			}
		}
		if (this.contentType === 'DEFAULT') {
			if (this.payloadObject) {
				ajaxConfig['headers'] = {
					...ajaxConfig['headers'],
					'Content-Type': 'application/json',
				};
			}
		} else if (this.contentType !== 'AUTO') {
			ajaxConfig['headers'] = {
				...ajaxConfig['headers'],
				'Content-Type': this.contentType,
			};
		}

		if (this.customHeaders) {
			ajaxConfig['headers'] = {
				...ajaxConfig['headers'],
				...this.customHeaders,
			};
		}
		// prevent seding the same requests at the same time
		const requestInProgress = isRequestInProgress(this.newRequestInProgress());

		//allowConsecutiveCalls will allow to make one more request with the new payload or headers in certain scenarios where the request body is exactly the same as previous request, but we need fresh response not the one in browser cache
		if (!requestInProgress || this.allowConsecutiveCalls) {
			const rxAjax$ = ajax(ajaxConfig)
				.pipe(
					map((response) => {
						this.checkForWrongApiResponse(response);
						setTimeout(() => {
							const requestInProgress = this.newRequestInProgress();
							if (isRequestInProgress(requestInProgress)) {
								dispatch(pullRequestInProgress(requestInProgress));
							}
						}, apiConfig.minDelayBetweenSameRequests);
						return response;
					}),
					catchError((error) => {
						this.handleErrors(error);
						if (this.logoutOnError) {
							AuthorizationService.logOut();
						}
						return throwError(error);
					})
				)
				.pipe(share())
				.pipe(
					map((response) => {
						this.cacheRequestIfNeeded(response);
						return this.asModel(response);
					})
				);
			dispatch(pushRequestInProgress(this.newRequestInProgress(rxAjax$)));
			return rxAjax$;
		} else if (requestInProgress.rxAjax$) {
			return requestInProgress.rxAjax$.pipe(publish(), refCount());
		} else {
			return new Observable();
		}
	}

	private onCallFromCache(response: any) {
		return of(response).pipe(
			map(
				(response) => {
					this.checkForWrongApiResponse(response);
					return this.asModel(response);
				},
				(e: Error) => console.log(this.errorMsg, e)
			)
		);
	}

	public call() {
		const { source, endpointConfig } = this.params;
		let responseFromCache: any;

		responseFromCache = isRequestCachedAndNotExpired({
			source,
			payloadHash: this.payloadHash,
			endpointUrl: endpointConfig.url,
		});

		if (responseFromCache) {
			return this.onCallFromCache(responseFromCache.response);
		} else {
			return this.onCall();
		}
	}

	public forceCall() {
		return this.onCall();
	}

	public setCustomerId() {
		const upId = store.getState()?.session?.user?.upId;
		this.customHeaders['x-customer-id'] = upId;
		return this;
	}
}
